import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import LaTeXRenderer from '../LaTeXRenderer';
import SubmissionGrader from './SubmissionGrader';
import SubmissionDetail from './SubmissionDetail';

const ScoreMatrix = ({ 
  user, 
  isGraderView = false, 
  onViewFile = null,
  students = null, // For grader view - pass all students
  allSubmissions = null, // For grader view - pass all submissions
  tests = null, // For grader view - pass all tests
  onOpenProblemModal = null // For grader view - problem modal handler (legacy, will be replaced)
}) => {
  // State for student view
  const [studentTests, setStudentTests] = useState([]);
  const [studentSubmissions, setStudentSubmissions] = useState([]);
  const [loading, setLoading] = useState(!isGraderView);
  const [error, setError] = useState('');

  // Universal feedback modal state
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [modalData, setModalData] = useState({
    student: null,
    test: null,
    problem: null,
    submission: null
  });

  // Fetch data for student view
  useEffect(() => {
    if (!isGraderView) {
      fetchStudentData();
    }
  }, [isGraderView]);

  const fetchStudentData = async () => {
    try {
      setLoading(true);
      
      // Fetch tests
      const testsResponse = await axios.get(`${API_BASE_URL}/api/tests`);
      const testsData = testsResponse.data;
      setStudentTests(testsData);

      // Fetch all student's submissions across all tests
      const submissionPromises = testsData.map(test => 
        axios.get(`${API_BASE_URL}/api/submissions/test/${test._id}`)
          .catch(err => ({ data: [] })) // Handle errors gracefully
      );
      
      const submissionResponses = await Promise.all(submissionPromises);
      const allStudentSubmissions = submissionResponses.flatMap(response => response.data);
      setStudentSubmissions(allStudentSubmissions);
      
    } catch (error) {
      console.error('Error fetching student score data:', error);
      setError('Failed to load score data');
    } finally {
      setLoading(false);
    }
  };

  // Universal feedback modal handlers
  const handleOpenFeedbackModal = async (studentId, testId, problemNumber) => {
    try {
      const submission = getSubmission(studentId, testId, problemNumber);
      
      if (!submission) {
        alert('No submission found for this problem');
        return;
      }

      // Find test, problem, and student details
      const currentTests = isGraderView ? tests : studentTests;
      const currentStudents = isGraderView ? students : [user];
      const test = currentTests.find(t => t._id === testId);
      const student = currentStudents.find(s => s._id === studentId);
      const problem = test?.problems?.find(p => p.problemNumber === problemNumber);

      setModalData({
        student,
        test,
        problem,
        submission
      });
      setShowFeedbackModal(true);
    } catch (error) {
      console.error('Error opening feedback modal:', error);
      alert('Failed to load submission details');
    }
  };

  const handleCloseFeedbackModal = () => {
    setShowFeedbackModal(false);
    setModalData({
      student: null,
      test: null,
      problem: null,
      submission: null
    });
  };

  const handleViewFile = async (submissionId, fileIndex, fileName) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/submissions/${submissionId}/download/${fileIndex}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      } else {
        alert('Failed to load file');
      }
    } catch (error) {
      console.error('File view error:', error);
      alert('Failed to load file');
    }
  };

  // Get submission for specific student, test, and problem
  const getSubmission = (studentId, testId, problemNumber) => {
    if (isGraderView && allSubmissions) {
      // Grader view - use passed submissions
      return allSubmissions.find(sub => 
        sub.student._id === studentId && 
        sub.test._id === testId && 
        sub.problemNumber === problemNumber
      );
    } else {
      // Student view - use local submissions
      return studentSubmissions.find(sub => 
        sub.test._id === testId && 
        sub.problemNumber === problemNumber
      );
    }
  };

  // Get problem status indicator for scoring matrix - universally clickable for all submissions
  const getProblemStatus = (studentId, testId, problemNumber) => {
    const submission = getSubmission(studentId, testId, problemNumber);
    
    if (!submission) {
      return (
        <div 
          style={{ 
            width: '32px', 
            height: '32px', 
            borderRadius: '4px', 
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            color: '#6c757d',
            cursor: 'default'
          }}
        >
          -
        </div>
      );
    }

    // Check if current user has provided feedback (for grader view only)
    const currentUserId = user?._id;
    const hasMyFeedback = isGraderView && submission.feedback?.some(f => f.gradedBy?._id === currentUserId);
    const needsMyResolution = hasMyFeedback && submission.status === 'pending_decision';

    let bgColor, textColor, content;
    
    if (submission.status === 'graded') {
      const score = submission.score || 0;
      if (score >= 5) {
        bgColor = '#28a745'; // Green for good scores
        textColor = 'white';
      } else if (score >= 3) {
        bgColor = '#ffc107'; // Yellow for medium scores
        textColor = '#000';
      } else {
        bgColor = '#dc3545'; // Red for low scores
        textColor = 'white';
      }
      content = score;
    } else if (submission.status === 'pending_decision') {
      bgColor = '#fd7e14'; // Orange for pending decision
      textColor = 'white';
      content = 'P';
    } else if (submission.status === 'submitted') {
      bgColor = '#17a2b8'; // Blue for submitted but not graded
      textColor = 'white';
      content = '!';
    } else {
      bgColor = '#6c757d'; // Gray for unknown status
      textColor = 'white';
      content = '?';
    }

    // Build tooltip text
    let tooltipText = `Problem ${problemNumber}: ${submission.status}`;
    if (submission.score !== null) {
      tooltipText += ` (Score: ${submission.score})`;
    }
    if (isGraderView && hasMyFeedback) {
      tooltipText += '\n✓ You have provided feedback';
      if (needsMyResolution) {
        tooltipText += '\n⚠️ Conflict needs your resolution';
      }
    }
    tooltipText += '\nClick to view submission & feedback';

    return (
      <div 
        style={{ 
          width: '32px', 
          height: '32px', 
          borderRadius: '4px', 
          backgroundColor: bgColor,
          border: hasMyFeedback ? '2px solid #000000' : '1px solid transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '11px',
          fontWeight: 'bold',
          color: textColor,
          cursor: 'pointer', // All submissions are now clickable
          position: 'relative',
          title: tooltipText
        }}
        onClick={() => {
          // For graders, use existing modal system if available, otherwise use universal
          if (isGraderView && onOpenProblemModal) {
            onOpenProblemModal(studentId, testId, problemNumber);
          } else {
            handleOpenFeedbackModal(studentId, testId, problemNumber);
          }
        }}
      >
        {content}
        {needsMyResolution && (
          <div style={{
            position: 'absolute',
            top: '-8px',
            right: '-8px',
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#000000',
            textShadow: '0 0 2px white, 0 0 4px white'
          }}>
            *
          </div>
        )}
      </div>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Use appropriate data source
  let testsToShow = isGraderView ? tests : studentTests;
  const studentsToShow = isGraderView ? students : [user];
  
  // For student view, only show tests where they have submitted at least one problem
  if (!isGraderView) {
    testsToShow = studentTests.filter(test => {
      // Check if student has any submissions for this test
      const hasSubmissions = studentSubmissions.some(sub => sub.test._id === test._id);
      return hasSubmissions;
    });
  }
  
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <div className="spinner"></div>
        <p>Loading score data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        {error}
      </div>
    );
  }

  if (!testsToShow || testsToShow.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
        <h3>No Submission Available</h3>
        <p>You will see your score table after submission.</p>
      </div>
    );
  }

  if (!studentsToShow || studentsToShow.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
        <h3>No Student Data</h3>
        <p>No student information available.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Legend for grader view */}
      {isGraderView && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>Score Matrix</h2>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', fontSize: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ width: '16px', height: '16px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '2px' }}></div>
              <span>Not submitted</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ width: '16px', height: '16px', backgroundColor: '#17a2b8', borderRadius: '2px' }}></div>
              <span>Needs grading</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ width: '16px', height: '16px', backgroundColor: '#fd7e14', borderRadius: '2px' }}></div>
              <span>Pending (P)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ width: '16px', height: '16px', backgroundColor: '#28a745', borderRadius: '2px' }}></div>
              <span>Good (5+)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ width: '16px', height: '16px', backgroundColor: '#ffc107', borderRadius: '2px' }}></div>
              <span>Medium (3-5)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ width: '16px', height: '16px', backgroundColor: '#dc3545', borderRadius: '2px' }}></div>
              <span>Low (0-2)</span>
            </div>
          </div>
        </div>
      )}

      {isGraderView ? (
        // Grader view - keep original multiple tables format
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          {testsToShow.map((test) => (
            <div key={test._id} className="card">
              <div style={{ marginBottom: '16px' }}>
                <h3 style={{ margin: '0 0 4px 0', color: '#333' }}>
                  {test.title}
                </h3>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  Week {test.week} • Due: {formatDate(test.dueDate)}
                </div>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse',
                  fontSize: '14px',
                  backgroundColor: 'white'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ 
                        padding: '12px 16px', 
                        textAlign: 'left', 
                        borderBottom: '2px solid #dee2e6',
                        fontWeight: '600',
                        minWidth: '200px'
                      }}>
                        Student
                      </th>
                      <th style={{ 
                        padding: '12px 8px', 
                        textAlign: 'center', 
                        borderBottom: '2px solid #dee2e6',
                        fontWeight: '600',
                        width: '60px'
                      }}>
                        P1
                      </th>
                      <th style={{ 
                        padding: '12px 8px', 
                        textAlign: 'center', 
                        borderBottom: '2px solid #dee2e6',
                        fontWeight: '600',
                        width: '60px'
                      }}>
                        P2
                      </th>
                      <th style={{ 
                        padding: '12px 8px', 
                        textAlign: 'center', 
                        borderBottom: '2px solid #dee2e6',
                        fontWeight: '600',
                        width: '60px'
                      }}>
                        P3
                      </th>
                      <th style={{ 
                        padding: '12px 8px', 
                        textAlign: 'center', 
                        borderBottom: '2px solid #dee2e6',
                        fontWeight: '600',
                        width: '60px'
                      }}>
                        P4
                      </th>
                      <th style={{ 
                        padding: '12px 16px', 
                        textAlign: 'center', 
                        borderBottom: '2px solid #dee2e6',
                        fontWeight: '600',
                        width: '120px'
                      }}>
                        Progress
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentsToShow.map((student) => {
                      // Calculate progress/scoring for this test
                      const problemStatuses = [1, 2, 3, 4].map(problemNum => {
                        const submission = getSubmission(student._id, test._id, problemNum);
                        return {
                          submitted: !!submission,
                          graded: submission?.status === 'graded',
                          score: submission?.score || 0
                        };
                      });
                      
                      const totalSubmitted = problemStatuses.filter(p => p.submitted).length;
                      const totalGraded = problemStatuses.filter(p => p.graded).length;
                      const needsGrading = problemStatuses.filter(p => p.submitted && !p.graded).length;

                      return (
                        <tr key={student._id} style={{ 
                          borderBottom: '1px solid #dee2e6'
                        }}>
                          <td style={{ 
                            padding: '12px 16px',
                            borderRight: '1px solid #eee'
                          }}>
                            <div>
                              <div style={{ fontWeight: '600', marginBottom: '2px' }}>
                                {student.name}
                              </div>
                              <div style={{ fontSize: '11px', color: '#666' }}>
                                ID: {student.studentId}
                              </div>
                              {student.role === 'grader' && (
                                <div style={{ fontSize: '10px', marginTop: '2px' }}>
                                  <span className="badge badge-warning" style={{ fontSize: '9px', padding: '1px 4px' }}>
                                    Grader
                                  </span>
                                </div>
                              )}
                            </div>
                          </td>
                          {[1, 2, 3, 4].map((problemNum) => (
                            <td key={problemNum} style={{ 
                              padding: '8px', 
                              textAlign: 'center',
                              borderRight: '1px solid #eee'
                            }}>
                              {getProblemStatus(student._id, test._id, problemNum)}
                            </td>
                          ))}
                          <td style={{ 
                            padding: '12px 16px', 
                            textAlign: 'center',
                            fontWeight: '600'
                          }}>
                            {totalSubmitted > 0 ? (
                              <div>
                                <div style={{ 
                                  fontSize: '14px', 
                                  color: needsGrading > 0 ? '#ffc107' : totalGraded === totalSubmitted ? '#28a745' : '#666'
                                }}>
                                  {totalGraded}/{totalSubmitted}
                                </div>
                                <div style={{ fontSize: '10px', color: '#666' }}>
                                  graded
                                </div>
                                {needsGrading > 0 && (
                                  <div style={{ fontSize: '9px', color: '#ffc107', marginTop: '2px' }}>
                                    {needsGrading} pending
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span style={{ color: '#999' }}>-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Test Summary */}
              <div style={{ 
                marginTop: '16px', 
                padding: '12px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '6px',
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '13px'
              }}>
                <div>
                  <strong>Test:</strong> {test.problems?.length || 4} problems
                </div>
                <div>
                  <span><strong>Students:</strong> {studentsToShow.length}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Student view - single consolidated table with test names as rows
        <div className="card">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '14px',
              backgroundColor: 'white'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ 
                    padding: '12px 16px', 
                    textAlign: 'left', 
                    borderBottom: '2px solid #dee2e6',
                    fontWeight: '600',
                    minWidth: '250px'
                  }}>
                    Mock Test
                  </th>
                  <th style={{ 
                    padding: '12px 8px', 
                    textAlign: 'center', 
                    borderBottom: '2px solid #dee2e6',
                    fontWeight: '600',
                    width: '60px'
                  }}>
                    P1
                  </th>
                  <th style={{ 
                    padding: '12px 8px', 
                    textAlign: 'center', 
                    borderBottom: '2px solid #dee2e6',
                    fontWeight: '600',
                    width: '60px'
                  }}>
                    P2
                  </th>
                  <th style={{ 
                    padding: '12px 8px', 
                    textAlign: 'center', 
                    borderBottom: '2px solid #dee2e6',
                    fontWeight: '600',
                    width: '60px'
                  }}>
                    P3
                  </th>
                  <th style={{ 
                    padding: '12px 8px', 
                    textAlign: 'center', 
                    borderBottom: '2px solid #dee2e6',
                    fontWeight: '600',
                    width: '60px'
                  }}>
                    P4
                  </th>
                  <th style={{ 
                    padding: '12px 16px', 
                    textAlign: 'center', 
                    borderBottom: '2px solid #dee2e6',
                    fontWeight: '600',
                    width: '120px'
                  }}>
                    Total Score
                  </th>
                  <th style={{ 
                    padding: '12px 8px', 
                    textAlign: 'center', 
                    borderBottom: '2px solid #dee2e6',
                    fontWeight: '600',
                    width: '80px'
                  }}>
                    Source & Link
                  </th>
                </tr>
              </thead>
              <tbody>
                {testsToShow.map((test) => {
                  const student = studentsToShow[0]; // Current user
                  // Calculate progress/scoring for this test
                  const problemStatuses = [1, 2, 3, 4].map(problemNum => {
                    const submission = getSubmission(student._id, test._id, problemNum);
                    return {
                      submitted: !!submission,
                      graded: submission?.status === 'graded',
                      score: submission?.score || 0
                    };
                  });
                  
                  const totalSubmitted = problemStatuses.filter(p => p.submitted).length;
                  const totalGraded = problemStatuses.filter(p => p.graded).length;
                  const needsGrading = problemStatuses.filter(p => p.submitted && !p.graded).length;
                  const totalScore = problemStatuses.reduce((sum, p) => sum + (p.graded ? p.score : 0), 0);
                  const maxPossibleScore = totalGraded * 7; // 7 is max score per problem

                  return (
                    <tr key={test._id} style={{ 
                      borderBottom: '1px solid #dee2e6'
                    }}>
                      <td style={{ 
                        padding: '12px 16px',
                        borderRight: '1px solid #eee'
                      }}>
                        <div>
                          <div style={{ fontWeight: '600', marginBottom: '2px' }}>
                            {test.title}
                          </div>
                          <div style={{ fontSize: '11px', color: '#666' }}>
                            Week {test.week} • Due: {formatDate(test.dueDate)}
                          </div>
                        </div>
                      </td>
                      {[1, 2, 3, 4].map((problemNum) => (
                        <td key={problemNum} style={{ 
                          padding: '8px', 
                          textAlign: 'center',
                          borderRight: '1px solid #eee'
                        }}>
                          {getProblemStatus(student._id, test._id, problemNum)}
                        </td>
                      ))}
                      <td style={{ 
                        padding: '12px 16px', 
                        textAlign: 'center',
                        fontWeight: '600'
                      }}>
                        {totalGraded > 0 ? (
                          <div>
                            <div style={{ 
                              fontSize: '16px', 
                              color: totalScore >= maxPossibleScore * 0.7 ? '#28a745' : 
                                     totalScore >= maxPossibleScore * 0.5 ? '#ffc107' : '#dc3545'
                            }}>
                              {totalScore}
                            </div>
                            <div style={{ fontSize: '10px', color: '#666' }}>
                              out of {maxPossibleScore}
                            </div>
                            {needsGrading > 0 && (
                              <div style={{ fontSize: '9px', color: '#17a2b8', marginTop: '2px' }}>
                                {needsGrading} pending
                              </div>
                            )}
                          </div>
                        ) : totalSubmitted > 0 ? (
                          <div style={{ color: '#17a2b8', fontSize: '12px' }}>
                            Grading...
                          </div>
                        ) : (
                          <span style={{ color: '#999' }}>-</span>
                        )}
                      </td>
                      <td style={{ 
                        padding: '8px', 
                        textAlign: 'center'
                      }}>
                        <Link 
                          to={`/test/${test._id}`}
                          style={{ 
                            color: '#007bff', 
                            textDecoration: 'none',
                            fontSize: '13px',
                            fontWeight: '500'
                          }}
                        >
                          View →
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Overall Summary for Student View */}
          <div style={{ 
            marginTop: '16px', 
            padding: '12px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '6px',
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '13px'
          }}>
            <div>
              <strong>Total Mock Tests:</strong> {testsToShow.length}
            </div>
            <div>
              <strong>Completed:</strong> {
                (() => {
                  const completedTests = testsToShow.filter(test => {
                    const userSubmissions = studentSubmissions.filter(sub => sub.test._id === test._id);
                    const gradedCount = userSubmissions.filter(sub => sub.status === 'graded').length;
                    return gradedCount === 4; // All 4 problems graded
                  });
                  return completedTests.length;
                })()
              }
            </div>
          </div>
        </div>
      )}

      {/* Help text for student view - positioned below the tables */}
      {!isGraderView && (
        <div style={{ marginTop: '20px', padding: '16px', backgroundColor: '#e7f3ff', borderRadius: '8px' }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '14px' }}>How to read your score matrix:</h4>
          <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', lineHeight: '1.5' }}>
            <li><strong>Numbers (0-7)</strong> are your final scores</li>
            <li><strong>Blue (!)</strong> means submitted but not yet graded</li>
            <li><strong>Orange P</strong> means graders are still deciding your score</li>
            <li><strong>Gray (-)</strong> means not submitted</li>
            <li><strong>Green</strong> = Good score (5-7), <strong>Yellow</strong> = Medium (3-4), <strong>Red</strong> = Low (0-2)</li>
          </ul>
        </div>
      )}

      {/* Simple Feedback Viewing Modal for Students/Teachers */}
      {showFeedbackModal && modalData.student && modalData.test && modalData.problem && modalData.submission && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            maxWidth: '90%',
            maxHeight: '90%',
            width: '700px',
            backgroundColor: 'white',
            borderRadius: '8px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #dee2e6',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#f8f9fa'
            }}>
              <div>
                <h3 style={{ margin: '0 0 4px 0' }}>
                  Problem {modalData.problem.problemNumber}: {modalData.problem.title}
                </h3>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  <strong>{modalData.student.name}</strong> (ID: {modalData.student.studentId}) • {modalData.test.title}
                </div>
              </div>
              <button
                onClick={handleCloseFeedbackModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ 
              flex: 1, 
              overflow: 'auto', 
              padding: '20px'
            }}>
              {/* Problem Description */}
              <div className="card" style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 12px 0' }}>Problem Statement</h4>
                {modalData.problem.description && (
                  <div style={{ marginBottom: '16px' }}>
                    <LaTeXRenderer text={modalData.problem.description} />
                  </div>
                )}
              </div>

              {/* Submission Information */}
              <div className="card" style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 12px 0', color: '#333' }}>
                  {isGraderView ? `${modalData.student.name}'s Submission` : 'Your Submission'}
                </h4>
                
                <div style={{ marginBottom: '16px', fontSize: '14px' }}>
                  <div><strong>Submitted:</strong> {formatDate(modalData.submission.submittedAt)}</div>
                  <div><strong>Status:</strong> {modalData.submission.status}</div>
                  {modalData.submission.score !== null && (
                    <div><strong>Final Score:</strong> <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#28a745' }}>{modalData.submission.score}/7</span></div>
                  )}
                </div>

                <SubmissionDetail
                  submission={modalData.submission}
                  maxScore={7}
                  onViewFile={handleViewFile}
                  currentUser={user}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px 20px',
              borderTop: '1px solid #dee2e6',
              backgroundColor: '#f8f9fa',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ fontSize: '13px', color: '#666' }}>
                Click any problem cell to view submission details and feedback
              </div>
              <button
                onClick={handleCloseFeedbackModal}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScoreMatrix;
